/**
 * Lumi beta coaching registry.
 *
 * Static, hand-authored coaching lines for the curated 13-item beta path
 * (10 lessons + 3 modules) plus per-Life-Event after-success and after-mistake
 * coaching. Pure data + lookup helpers — no React, no AI calls, no network.
 *
 * Lookups are deterministic, so coaching never regenerates on scroll or
 * remount. The cacheKey on each line is what `useLumiVoice` keys audio by
 * upstream, so repeat voice playback hits the existing URL cache.
 */
import { launchLessonIds } from "@/lib/launch/launchExperienceRoster";
import { betaLifeEvents } from "@/data/launchLifeEvents";

export type LumiCoachingType =
  | "encouragement"
  | "correction"
  | "risk_warning"
  | "next_step"
  | "reflection";

export interface LumiCoachingLine {
  cacheKey: string;
  title: string;
  message: string;
  coachingType: LumiCoachingType;
  suggestedAction?: string;
}

type LessonOutcome = "before" | "after_success" | "after_mistake";
type ModuleKey = "budget_builder" | "credit_management" | "scam_defense";

const LID = launchLessonIds;

function line(
  cacheKey: string,
  title: string,
  message: string,
  coachingType: LumiCoachingType,
  suggestedAction?: string,
): LumiCoachingLine {
  return { cacheKey, title, message, coachingType, suggestedAction };
}

/** lessonId -> outcome -> line */
const LESSON_COACHING: Record<string, Record<LessonOutcome, LumiCoachingLine>> = {
  [LID[0]]: {
    before: line("lumi:l0:before", "Needs vs Wants",
      "Today we’re separating what feels important from what actually has to be handled first.",
      "next_step"),
    after_success: line("lumi:l0:ok", "Strong move",
      "You protected the essentials first. That is how strong money decisions start.",
      "encouragement"),
    after_mistake: line("lumi:l0:miss", "Let’s rethink that",
      "That choice makes sense emotionally, but the key move is protecting your must-pay items before spending on wants.",
      "correction"),
  },
  [LID[1]]: {
    before: line("lumi:l1:before", "Opportunity Cost",
      "Every choice has a hidden tradeoff. Let’s practice spotting what you give up.",
      "next_step"),
    after_success: line("lumi:l1:ok", "Clear thinking",
      "You saw the tradeoff clearly. That’s real decision intelligence.",
      "encouragement"),
    after_mistake: line("lumi:l1:miss", "Name the tradeoff",
      "You looked at the choice in isolation. Try asking: ‘What am I giving up if I choose this?’",
      "correction"),
  },
  [LID[2]]: {
    before: line("lumi:l2:before", "Financial Tradeoffs",
      "Tradeoffs are not about always saying no. They are about choosing what creates the most value.",
      "next_step"),
    after_success: line("lumi:l2:ok", "Value-first thinking",
      "You compared the long-term value, not just the immediate reward.",
      "encouragement"),
    after_mistake: line("lumi:l2:miss", "Look further out",
      "That option may feel good now, but compare it against what else the money could do.",
      "correction"),
  },
  [LID[3]]: {
    before: line("lumi:l3:before", "Delayed Gratification",
      "Waiting is not weakness. Sometimes waiting is how you keep control.",
      "next_step"),
    after_success: line("lumi:l3:ok", "Patience pays",
      "You used patience as a strategy. That is a powerful financial habit.",
      "encouragement"),
    after_mistake: line("lumi:l3:miss", "Pause before deciding",
      "The instant reward was tempting, but waiting could have protected your cash.",
      "correction"),
  },
  [LID[4]]: {
    before: line("lumi:l4:before", "Cash Flow",
      "Cash flow is the map. It shows where your money is going before it disappears.",
      "next_step"),
    after_success: line("lumi:l4:ok", "Planned before spending",
      "You planned before spending. That is how you avoid surprise shortfalls.",
      "encouragement"),
    after_mistake: line("lumi:l4:miss", "Start with the map",
      "You made the decision before checking the full money picture. Start with what is coming in and what must go out.",
      "correction"),
  },
  [LID[5]]: {
    before: line("lumi:l5:before", "Credit Score Basics",
      "Credit is a trust signal. The goal is to show lenders you can manage money consistently.",
      "next_step"),
    after_success: line("lumi:l5:ok", "Strong credit habits",
      "You protected the two big credit habits: paying on time and keeping balances low.",
      "encouragement"),
    after_mistake: line("lumi:l5:miss", "Lenders are watching",
      "That choice could make you look riskier to lenders. Focus on payment history and low utilization.",
      "correction"),
  },
  [LID[6]]: {
    before: line("lumi:l6:before", "Borrowing Money",
      "Borrowing can help or hurt. The difference is whether you understand the real cost.",
      "next_step"),
    after_success: line("lumi:l6:ok", "True-cost thinker",
      "You looked beyond the payment and found the true cost. That is smart borrowing.",
      "encouragement"),
    after_mistake: line("lumi:l6:miss", "Watch the total cost",
      "The monthly payment looked smaller, but the total repayment cost matters more.",
      "correction"),
  },
  [LID[7]]: {
    before: line("lumi:l7:before", "Minimum Payments Trap",
      "Minimum payments can feel safe, but they can quietly keep debt alive.",
      "risk_warning"),
    after_success: line("lumi:l7:ok", "You beat the trap",
      "You reduced the trap instead of just meeting the lowest requirement.",
      "encouragement"),
    after_mistake: line("lumi:l7:miss", "Minimums are slow leaks",
      "The minimum keeps the account current, but it may cost much more over time.",
      "correction"),
  },
  [LID[8]]: {
    before: line("lumi:l8:before", "Urgency as a Red Flag",
      "Scammers use pressure to rush your brain. Your defense is slowing the moment down.",
      "risk_warning"),
    after_success: line("lumi:l8:ok", "You broke the pressure loop",
      "You broke the pressure loop. That is one of the strongest scam defense moves.",
      "encouragement"),
    after_mistake: line("lumi:l8:miss", "Slow it down",
      "The urgency was designed to make you react before verifying. Slow down and check through a trusted source.",
      "correction"),
  },
  [LID[9]]: {
    before: line("lumi:l9:before", "Too Good to Be True",
      "When an offer promises huge rewards with no risk, your guard should go up.",
      "risk_warning"),
    after_success: line("lumi:l9:ok", "Smart skepticism",
      "You questioned the promise instead of chasing it. That protected your money and your information.",
      "encouragement"),
    after_mistake: line("lumi:l9:miss", "Manufactured proof",
      "The proof looked convincing, but scammers often manufacture proof to make the lie feel real.",
      "correction"),
  },
};

const MODULE_COACHING: Record<ModuleKey, Record<LessonOutcome, LumiCoachingLine>> = {
  budget_builder: {
    before: line("lumi:m:bb:before", "Budget Builder",
      "This is where your budget has to survive real life, not just look good on paper.",
      "next_step"),
    after_success: line("lumi:m:bb:ok", "You adapted well",
      "You adapted without losing control. That is exactly what a real budget should help you do.",
      "encouragement"),
    after_mistake: line("lumi:m:bb:miss", "Adjust without panic",
      "A budget is not broken because something unexpected happened. The skill is adjusting without panic.",
      "correction"),
  },
  credit_management: {
    before: line("lumi:m:cm:before", "Credit Management",
      "Now we’re managing credit like a system: timing, balances, utilization, and payment behavior.",
      "next_step"),
    after_success: line("lumi:m:cm:ok", "Managed the signal",
      "You managed the balance before it became a bigger credit signal.",
      "encouragement"),
    after_mistake: line("lumi:m:cm:miss", "Timing matters",
      "That move could raise risk because timing and utilization both matter.",
      "correction"),
  },
  scam_defense: {
    before: line("lumi:m:sd:before", "Scam Defense",
      "This is your real-world defense test. Look for the signal hidden inside the message.",
      "risk_warning"),
    after_success: line("lumi:m:sd:ok", "Verified independently",
      "You avoided the trap and verified independently. That is real scam defense.",
      "encouragement"),
    after_mistake: line("lumi:m:sd:miss", "Always verify upstream",
      "The page looked familiar, but the safest path is always going directly to the official source.",
      "correction"),
  },
};

function moduleKeyFromTitle(title: string): ModuleKey | null {
  const t = title.toLowerCase();
  if (t.includes("budget")) return "budget_builder";
  if (t.includes("credit")) return "credit_management";
  if (t.includes("scam")) return "scam_defense";
  return null;
}

// ───────── Lesson lookups ─────────

export function getLumiLessonIntro(lessonId: string): LumiCoachingLine | null {
  return LESSON_COACHING[lessonId]?.before ?? null;
}

export function getLumiLessonOutcome(
  lessonId: string,
  outcome: "success" | "mistake",
): LumiCoachingLine | null {
  const rec = LESSON_COACHING[lessonId];
  if (!rec) return null;
  return outcome === "success" ? rec.after_success : rec.after_mistake;
}

export function getLumiModuleIntro(title: string): LumiCoachingLine | null {
  const key = moduleKeyFromTitle(title);
  return key ? MODULE_COACHING[key].before : null;
}

export function getLumiModuleOutcome(
  title: string,
  outcome: "success" | "mistake",
): LumiCoachingLine | null {
  const key = moduleKeyFromTitle(title);
  if (!key) return null;
  return outcome === "success"
    ? MODULE_COACHING[key].after_success
    : MODULE_COACHING[key].after_mistake;
}

// ───────── Life event lookups ─────────

/**
 * Build coaching from the existing life event content pack so we don't
 * fork copy. Anchored coaching uses the lesson/module coaching when the
 * event anchors to one, falling back to a generic risk/encouragement line.
 */
export function getLumiLifeEventCoaching(
  eventId: string,
  correct: boolean,
): LumiCoachingLine | null {
  const ev = betaLifeEvents.find((e) => e.id === eventId);
  if (!ev) return null;

  let base: LumiCoachingLine | null = null;
  if (ev.anchor.kind === "lesson") {
    base = getLumiLessonOutcome(ev.anchor.lessonId, correct ? "success" : "mistake");
  } else {
    base = getLumiModuleOutcome(ev.anchor.title, correct ? "success" : "mistake");
  }

  if (base) {
    return {
      ...base,
      cacheKey: `lumi:lifeevent:${ev.id}:${correct ? "ok" : "miss"}`,
    };
  }

  return correct
    ? line(
        `lumi:lifeevent:${ev.id}:ok`,
        "Real-world win",
        `You ${ev.skillGained.toLowerCase()}. ${ev.whyItMattered}`,
        "encouragement",
      )
    : line(
        `lumi:lifeevent:${ev.id}:miss`,
        "Tough call — let’s learn from it",
        "That choice is common because the pressure feels real. But pressure is exactly why we pause, verify, and protect the money first.",
        "risk_warning",
        `Practice: ${ev.skillGained}`,
      );
}

// ───────── Dashboard next-step ─────────

export interface BetaDashboardStats {
  totalLessons: number;
  completedLessons: number;
  unlockedModules: number;
  recentMistakeHeavy?: boolean;
}

export function getLumiDashboardCoaching(stats: BetaDashboardStats): LumiCoachingLine {
  const { totalLessons, completedLessons, recentMistakeHeavy } = stats;
  if (completedLessons === 0) {
    return line(
      "lumi:dash:new",
      "Welcome to your beta path",
      "I’ll guide you through the beta path one step at a time. Start with the first lesson and focus on making decisions, not memorizing terms.",
      "next_step",
    );
  }
  if (totalLessons > 0 && completedLessons >= totalLessons) {
    return line(
      "lumi:dash:strong",
      "You’re building real confidence",
      "You made strong decisions today. Let’s keep building that real-world confidence.",
      "encouragement",
    );
  }
  if (recentMistakeHeavy) {
    return line(
      "lumi:dash:recover",
      "Nice progress — let’s slow the tricky moments",
      "You made progress today. Next time, we’ll slow down around choices that involve pressure, debt, or hidden costs.",
      "reflection",
    );
  }
  return line(
    "lumi:dash:returning",
    "Your next beta step is ready",
    "You’re building a pattern. Your next beta step is ready.",
    "next_step",
  );
}

// ───────── Decide / Quiz miss coaching ─────────

/**
 * Lightweight coaching for a missed Decide / Quiz question. Anchored to
 * the host lesson so wording stays on-concept. Falls back to a generic
 * reflection if the lesson isn't in the beta roster.
 */
export function getLumiDecideMissCoaching(lessonId: string): LumiCoachingLine {
  const lesson = LESSON_COACHING[lessonId]?.after_mistake;
  if (lesson) {
    return {
      ...lesson,
      cacheKey: `lumi:decide-miss:${lessonId}`,
    };
  }
  return line(
    `lumi:decide-miss:${lessonId}`,
    "Let’s break this one down",
    "Looks like this one came down to the core concept of the lesson. Re-read the question, compare what you picked against what you had to give up, and try again.",
    "correction",
  );
}

export function getLumiDecidePassCoaching(lessonId: string): LumiCoachingLine {
  const lesson = LESSON_COACHING[lessonId]?.after_success;
  if (lesson) {
    return {
      ...lesson,
      cacheKey: `lumi:decide-pass:${lessonId}`,
    };
  }
  return line(
    `lumi:decide-pass:${lessonId}`,
    "Knowledge check cleared",
    "You cleared this check. The next step is applying it when the situation feels real.",
    "encouragement",
  );
}